import { List } from "immutable"
import 'intersection-observer'
import * as React from "react"
import { InView } from 'react-intersection-observer'
import { IOWidget, Updater, Widget, fromJSX } from "widgets-for-react"
import { Link } from "../../../../cms/blocks/types.gen"
import { getClassName, link } from "../../../../cms/blocks/utils.gen"
import { toggleExpandable } from "../../../../cms/blocks/widgets/expandBlock.gen"
import { blockInviewHandler } from "../../../../drupal/rendering/inview_utils.mod"
import { ParsedLinkItem, ParsedMediaDocument } from "../../../../drupal/types_and_parser/types"
import { CMSState } from "../../../drupalPage/state"
import { PublicSiteState } from "../../../state"

export type expandBlockProps = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: expandProps[]
  link?: Link
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type expandProps = {
  index: number
  key?: string
  title: string
  subtitle?: React.ReactNode
  links: List<ParsedLinkItem>
  downloads: List<ParsedMediaDocument>
  text?: string
  open: boolean
  useSVGIcons: boolean
}

export type expandBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  list_classname: string
  footer_classname: string
  clickable_classname: string
  expand_item_classname: string
  expand_details_classname: string
  expand_summary_classname: string
  expand_title_classname: string
  expand_subtitle_classname: string
  expand_icon_classname: string
  expand_text_classname: string
  icon_open_id: string
  icon_closed_id: string
}>

export function expandBlock<customBlockData extends { kind: customBlockData["kind"] }, extraImages>(
  props: expandBlockProps,
  options?: { style?: expandBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (s: CMSState) => fromJSX(
      onDone => {
        return (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('expand', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={getClassName(options, "block_classname", "container")}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className={getClassName(options, "inner_classname", "py-5")}>
              
              {(props.title || props.intro) && (
                <header className={getClassName(options, "header_classname", "")}>
                  {props.title && <h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>}
                  {props.intro && <p className={getClassName(options, "intro_classname", "")}>{props.intro}</p>}
                </header>
              )}

              <div className={getClassName(options, "content_classname", "")}>
                <ul className={getClassName(options, "list_classname", "accordion list-unstyled mb-3")}>
                  {props.items.map((value, index) =>
                    Expand(
                      s,
                      {
                        index: props.index,
                        key: `${options && options.key ? options.key : props.index}-${index}`,
                        text: value.text,
                        open: value.open,
                        title: value.title,
                        links: value.links,
                        downloads: value.downloads,
                        subtitle: value.subtitle,
                        useSVGIcons: value.useSVGIcons
                      },
                      props,
                      index,
                      options
                    ).run(action => {
                      let openNew = action(value.open)
                      onDone(s1 => {
                        let currentBlock = s1.blocks.get(props.index)
                        if (currentBlock.kind === "expandBlock") {
                          currentBlock = {
                            ...currentBlock,
                            value: [
                              {
                                ...currentBlock.value[0],
                                items: currentBlock.value[0].items.map(
                                  (e:any, i: number) => (i === index ? { ...e, open: openNew } : e)
                                ),
                              },
                              currentBlock.value[1]
                            ],
                          }
                        }
                        return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
                      })
                    })
                  )}
                </ul>
              </div>

              {props.link &&
                <footer className={getClassName(options, "footer_classname", "")}> 
                  {link(props.link,
                        {classname: getClassName(options, "clickable_classname", "btn btn-link pl-0")},
                        props.link.title,
                        () => {
                          window.dataLayer.push({
                            event: 'event.refresco',
                            category: 'ExpandableBlock',
                            action: props.link.title,
                            label: window.location.pathname,
                          })
                        })}
                </footer>
              }
            </div>
          </InView>
        )
      },
    )
}

  function Expand<CustomBlockData extends { kind: CustomBlockData["kind"] }, extra_images>(
    s: CMSState,
    props: expandProps,
    blockProps: expandBlockProps,
    index: number,
    options?: { style?: expandBlockStyle; key?: string }
  ): Widget<Updater<boolean>> {
    return fromJSX(
      onDone => 
        <li 
          className={getClassName(options, "expand_item_classname", "card")} 
          key={`expand-${options && options.key ? options.key : props.index}-${index}`}
        >
          <details className={getClassName(options, 'expand_details_classname', '' )}>
            <summary
              onClick={(event) => {
                toggleExpandable(event);
                if (!props.open)
                window.dataLayer.push({
                  event: "event.refresco", category: "Expandable", action: props.title, label: window.location.pathname
                });
                onDone((s) => !s)
              }}
              className={getClassName(options, "expand_summary_classname", `card-header ${props.open ? "col--open" : "col--closed"} text-left border-0`)}
              aria-controls={`a11y-${props.key}`}
              aria-expanded={props.open}
              aria-label="Toggle item"
            >
              <p className={getClassName(options, "expand_title_classname", "btn btn-link m-0 p-0")}>{props.title}</p>
              {props.subtitle && (<p className={getClassName(options, "expand_subtitle_classname", "text-muted m-0")}>{props.subtitle}</p>)}
              {props.useSVGIcons && (
                <svg className={getClassName(options, "expand_icon_classname", "mr-3")} aria-hidden="true">
                  <use xlinkHref={`${props.open ? getClassName(options, "icon_open_id", "#icon--chevron-up") : getClassName(options, "icon_closed_id", "#icon--chevron-down")}`}/>
                </svg>
              )}
            </summary>
          
            <div
              id={`a11y-${props.key}`}
              className={getClassName(options, "expand_text_classname", `card-body pb-1 ${props.open ? "" : "d-none"} text-left border-0`)}
              aria-hidden={!props.open}
            >
              {props.text && <div className="text--styled" dangerouslySetInnerHTML={{ __html: props.text }} />}
              {props.links.size > 0 &&
                <div className="block--link block--lists tho">
                <ul className="block__list"> 
                  {props.links.map(link => 
                    <li className="block__item blocklist__item">
                      <a className="block__link" href={link.url} target="blank">
                        <span className="blocklist__title icon--link">{link.title}</span>
                      </a>
                    </li>
                  ).toArray()}
                  </ul>
                </div>
              }
              {props.downloads.size > 0 && 
                <div className="block--download block--lists">
                  <ul className="block__list">
                  {props.downloads.map(link => 
                    <li className="block__item blocklist__item">
                      <a className="block__link" href={link.field_media_file.file.url} target="blank">
                        <span className="blocklist__title">
                          {link.label}
                          </span>
                        </a>
                    </li>
                  ).toArray()}
                  </ul>
                </div>
              }

            </div>
          </details>
      </li>
  )
}
